import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  KeyRound,
  Mail,
  ShieldCheck,
  UserPlus
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

const BrowserMockup = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
    <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
      <div className="flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-full bg-rose-300" />
        <span className="h-3 w-3 rounded-full bg-amber-300" />
        <span className="h-3 w-3 rounded-full bg-emerald-300" />
      </div>
      <div className="text-xs text-gray-500 font-medium">{title}</div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const StepCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6">
    <div className="flex items-start gap-4">
      <div className="h-12 w-12 rounded-2xl bg-gray-50 border border-gray-200 text-[#141414] flex items-center justify-center flex-shrink-0">
        <Icon size={20} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-[#141414]">{title}</h3>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
      </div>
    </div>
  </div>
);

const NexbotOpenrouterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const steps = t('nexbot.openrouter.steps', { returnObjects: true }).map((step, index) => ({
    ...step,
    icon: [UserPlus, KeyRound, Copy, CreditCard, ShieldCheck][index] || KeyRound
  }));
  const needsItems = t('nexbot.openrouter.needs.items', { returnObjects: true });
  const outcomeItems = t('nexbot.openrouter.outcomes.items', { returnObjects: true });
  const importantItems = t('nexbot.openrouter.important.items', { returnObjects: true });
  const securityItems = t('nexbot.openrouter.security.items', { returnObjects: true });
  const resources = t('nexbot.openrouter.resources.cards', { returnObjects: true });

  return (
    <div className="min-h-screen bg-white/60">
      <PageHeader hideCTA={true} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/nexbot'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('nexbot.openrouter.back')}
          </button>

          <section className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 mb-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                <KeyRound size={16} />
                {t('nexbot.openrouter.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-[#141414] tracking-tight mb-4">
                {t('nexbot.openrouter.title')}
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl">
                {t('nexbot.openrouter.subtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="https://openrouter.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('nexbot.openrouter.cta_open')}
                  <ExternalLink size={16} />
                </a>
                <a
                  href="#pasos"
                  className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('nexbot.openrouter.cta_steps')}
                </a>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-3xl p-6">
                <div className="text-sm font-semibold text-gray-900">{t('nexbot.openrouter.needs.title')}</div>
                <div className="mt-4 space-y-3">
                  {needsItems.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-3xl p-6">
                <div className="text-sm font-semibold text-gray-900">{t('nexbot.openrouter.outcomes.title')}</div>
                <div className="mt-4 space-y-3">
                  {outcomeItems.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-sm text-gray-700">
                      <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#141414] text-white rounded-3xl p-6 border border-white/10">
                <div className="text-sm font-semibold text-white">{t('nexbot.openrouter.important.title')}</div>
                <div className="mt-4 space-y-3 text-sm text-white/80 leading-relaxed">
                  {importantItems.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="pasos" className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {steps.map((step) => (
                <StepCard key={step.title} icon={step.icon} title={step.title} desc={step.desc} />
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <BrowserMockup title={t('nexbot.openrouter.mockups.register.browser_title')}>
                <div className="space-y-4">
                  <div className="text-lg font-semibold text-[#141414]">{t('nexbot.openrouter.mockups.register.title')}</div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                      {t('nexbot.openrouter.mockups.register.google')}
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                      {t('nexbot.openrouter.mockups.register.github')}
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-400">
                      {t('nexbot.openrouter.mockups.register.email')}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-[#141414] text-white px-4 py-3 text-sm font-semibold text-center">
                    {t('nexbot.openrouter.mockups.register.cta')}
                  </div>
                </div>
              </BrowserMockup>

              <BrowserMockup title={t('nexbot.openrouter.mockups.keys.browser_title')}>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold text-[#141414]">{t('nexbot.openrouter.mockups.keys.title')}</div>
                      <div className="text-sm text-gray-500">{t('nexbot.openrouter.mockups.keys.subtitle')}</div>
                    </div>
                    <div className="rounded-2xl bg-[#141414] text-white px-4 py-2 text-sm font-semibold">
                      {t('nexbot.openrouter.mockups.keys.cta')}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-gray-200 p-4">
                    <div className="text-sm font-semibold text-gray-900">{t('nexbot.openrouter.mockups.keys.name_label')}</div>
                    <div className="mt-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
                      {t('nexbot.openrouter.mockups.keys.name_value')}
                    </div>
                    <div className="mt-4 text-sm font-semibold text-gray-900">{t('nexbot.openrouter.mockups.keys.limit_label')}</div>
                    <div className="mt-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-400">
                      {t('nexbot.openrouter.mockups.keys.limit_value')}
                    </div>
                  </div>
                </div>
              </BrowserMockup>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div>
                  <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                    <ShieldCheck size={16} />
                    {t('nexbot.openrouter.security.badge')}
                  </div>
                  <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.openrouter.security.title')}</h2>
                  <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                    {securityItems.map((item) => (
                      <p key={item}>{item}</p>
                    ))}
                  </div>
                </div>
                <BrowserMockup title={t('nexbot.openrouter.mockups.token.browser_title')}>
                  <div className="space-y-4">
                    <div className="text-sm font-semibold text-gray-900">{t('nexbot.openrouter.mockups.token.title')}</div>
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4 text-sm text-emerald-900 font-medium break-all">
                      sk-or-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
                        {t('nexbot.openrouter.mockups.token.copy')}
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
                        {t('nexbot.openrouter.mockups.token.limit')}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('nexbot.openrouter.mockups.token.note')}
                    </div>
                  </div>
                </BrowserMockup>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-violet-100">
                <Mail size={16} />
                {t('nexbot.openrouter.resources.badge')}
              </div>
              <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.openrouter.resources.title')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.map((resource) => (
                  <div key={resource.title} className="bg-gray-50 border border-gray-200 rounded-3xl p-6">
                    <div className="text-lg font-semibold text-[#141414]">{resource.title}</div>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{resource.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/nexbot"
                  className="inline-flex items-center justify-center bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('nexbot.openrouter.back_to_nexbot')}
                </Link>
                <a
                  href="https://openrouter.ai/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('nexbot.openrouter.open_keys')}
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NexbotOpenrouterPage;
